/**
 * Action Orchestrator
 *
 * Enhanced orchestrator with async/polling support and automatic toast notifications.
 * Auto-detects processor capabilities and routes to sync or async execution.
 * Provides centralized toast feedback for all actions.
 */

import {
  Action,
  ProcessorResult,
  ProgressCallback,
} from '@/business/types/action.types';
import { ValidationResult } from '@/business/types/processor.types';
import { logger, generateRequestId, LogLayer } from '@/utils/logger';
import { beginRequest, endRequest } from '@/utils/request-context';
import { getResponseCodeMessage } from '@/business/constants/response-codes';
import { ResponseCode } from '@/business/types/response-codes.types';
import {
  asyncActionManager,
  ProgressCallback as AsyncProgressCallback,
} from '@/business/utils/async-action-manager';
import {
  AsyncPollResponse,
  AsyncRequestStatus,
} from '@/business/types/async.types';
import { Toast, ToastType } from '@/types/toast.types';
import { getActionToastConfig } from '@/business/config/action-toast-config';
import { getActionConfig } from '@/business/config/action-configs';

/**
 * Callback function for action results.
 * Called by orchestrator after interpreting the processor result.
 *
 * @param code Response code from the processor
 * @param message User-friendly message for the code
 * @param data Optional data from the processor (present for success codes)
 */
export type ResultCallback<TOutput = unknown> = (
  code: ResponseCode,
  message: string,
  data?: TOutput
) => void;

/**
 * Toast methods interface
 * Matches the ToastContextValue from ToastContext
 */
export interface ToastMethods {
  loading: (title: string, options?: any) => string;
  update: (
    id: string,
    updates: Partial<Pick<Toast, 'type' | 'title' | 'message' | 'duration'>>
  ) => void;
  success: (title: string, options?: any) => string;
  error: (title: string, options?: any) => string;
}

/**
 * Toast configuration for action execution
 */
export interface ActionToastConfig {
  /**
   * Whether to show toasts for this action
   * - true: Show toasts (default for most actions)
   * - false: Silent execution
   * - undefined: Use default from action config
   */
  enabled?: boolean;

  /** Custom loading message (overrides default) */
  loading?: string;

  /** Custom success message (overrides default) */
  success?: string;

  /** Custom error message (overrides default) */
  error?: string;
}

/**
 * Enhanced execute options with result callback and toast support
 */
export interface OrchestratorExecuteOptions<TOutput = unknown> {
  /** Progress callback */
  onProgress?: ProgressCallback;

  /** Result callback - receives interpreted result */
  onResult?: ResultCallback<TOutput>;

  /**
   * Toast configuration for this execution
   * - undefined: Use default toast messages from action config
   * - false: Disable toasts for this call
   * - ActionToastConfig: Custom toast messages
   */
  toast?: ActionToastConfig | false;

  /**
   * Toast methods for showing notifications
   * If not provided, toasts will be disabled for this execution
   */
  toastMethods?: ToastMethods;
}

/**
 * Orchestrator for executing actions with async/polling support.
 *
 * Features:
 * - Auto-detects processor capabilities (sync vs async)
 * - Dual-path execution (sync and async)
 * - Two-layer validation (infrastructure + business logic)
 * - Response code integration
 * - Enhanced progress tracking
 * - Exponential backoff polling
 *
 * @example
 * ```typescript
 * const orchestrator = ActionOrchestrator.getInstance();
 *
 * // Works with both sync and async processors
 * const result = await orchestrator.execute(
 *   Action.LOGIN,
 *   { username, password, school },
 *   {
 *     onProgress: (p, msg) => console.log(`${p}%: ${msg}`),
 *     onStatusChange: (status) => console.log(`Status: ${status}`),
 *     timeout: 30000,
 *   }
 * );
 * ```
 */
export class ActionOrchestrator {
  private static instance: ActionOrchestrator;

  private constructor() {
    logger.info('ActionOrchestrator initialized');
  }

  /**
   * Get singleton instance of ActionOrchestrator
   */
  public static getInstance(): ActionOrchestrator {
    if (!ActionOrchestrator.instance) {
      ActionOrchestrator.instance = new ActionOrchestrator();
    }
    return ActionOrchestrator.instance;
  }

  /**
   * Execute an action using async API (submit and poll).
   * NEVER THROWS - Always returns processor result.
   *
   * The orchestrator:
   * 1. Validates input data
   * 2. Submits action via AsyncActionManager
   * 3. Polls for completion with progress updates
   * 4. Converts poll response to ProcessorResult
   * 5. Calls result callback with code, message, and data
   *
   * @template TInput The type of input data
   * @template TOutput The type of output data
   * @param action The action to execute
   * @param data Input data
   * @param options Execute options with callbacks
   * @returns Promise resolving to processor result
   */
  public async execute<TInput = unknown, TOutput = unknown>(
    action: Action,
    data: TInput,
    options?: OrchestratorExecuteOptions<TOutput>
  ): Promise<ProcessorResult<TOutput>> {
    const startTime = Date.now();
    const requestId = generateRequestId();
    const log = logger.withRequest(requestId, LogLayer.ORCHESTRATOR);

    log.info('Action execution started (async mode)', {
      action,
      hasProgressCallback: !!options?.onProgress,
      hasResultCallback: !!options?.onResult,
    });

    // Toast setup
    let currentToastId: string | null = null;
    const toastMethods = options?.toastMethods;
    const toastConfig = getActionToastConfig(action);
    const customToast =
      typeof options?.toast === 'object' ? options.toast : undefined;
    const shouldShowToast =
      toastMethods && options?.toast !== false && toastConfig.enabled;

    // Show loading toast if enabled
    if (shouldShowToast && (customToast?.loading || toastConfig.loading)) {
      const loadingMessage =
        customToast?.loading || toastConfig.loading || 'Processing...';
      currentToastId = toastMethods.loading(loadingMessage);
    }

    // Lets the http client tag its log lines with this action's request id.
    // Directly before the try so the finally always pairs with it.
    beginRequest(requestId);
    try {
      // Step 1: Get action config
      const config = getActionConfig<TInput, unknown, TOutput>(action);

      log.debug('Action config retrieved', { action });

      // Step 2: Auto-inject organization_id before validation (fallback only).
      // If the caller explicitly provides organization_id (e.g. DELETE_ORGANIZATION
      // targeting a specific org), that value is preserved. Otherwise the active
      // org from the provider is used.
      const orgId = asyncActionManager.getOrganizationId();
      let fullData: TInput;
      if (orgId) {
        const dataRecord = data as Record<string, unknown>;
        fullData = {
          organization_id: orgId,
          ...dataRecord,
        } as TInput;
      } else {
        fullData = data as TInput;
      }

      // Step 3: Validate input data using config validator
      log.debug('Validating input data', { action });
      let validationResult: ValidationResult = { isValid: true };

      if (config.validate) {
        validationResult = config.validate(fullData);
      }

      if (!validationResult.isValid) {
        log.warn('Input validation failed', {
          action,
          errors: validationResult.errors,
        });

        const result: ProcessorResult<TOutput> = {
          code: ResponseCode.VALIDATION_FAILED,
          data: undefined,
        };

        // Update toast on validation failure
        if (shouldShowToast && currentToastId) {
          const errorMessage =
            customToast?.error ||
            toastConfig.error ||
            getResponseCodeMessage(result.code);
          toastMethods.update(currentToastId, {
            type: ToastType.ERROR,
            title: errorMessage,
            duration: 5000,
          });
        }

        // Call result callback
        this.handleResult(result, options?.onResult);

        return result;
      }

      log.debug('Input validation passed', { action });

      // Step 3: Submit action and poll for completion
      const asyncProgressCallback: AsyncProgressCallback = (
        status,
        metadata
      ) => {
        // Map async status to progress percentage
        const progress = this.getProgressFromStatus(status);
        // Get message from config instead of hardcoded method
        const message = config.progressMessages[status];

        // Call user's progress callback
        options?.onProgress?.(progress, message);

        log.debug('Async status transition', {
          status,
          server_request_id: metadata?.requestId,
          metadata,
        });
      };

      // Backend might return a different type than TOutput if transformation is needed
      const pollResponse = await asyncActionManager.submitAndWait<unknown>(
        action,
        fullData as Record<string, unknown>,
        asyncProgressCallback
      );

      const duration = Date.now() - startTime;

      // Step 4: Convert poll response to ProcessorResult (with backend data type)
      const backendResult =
        this.convertPollResponseToResult<unknown>(pollResponse);

      // Step 5: Apply response transformation if configured, or cast to TOutput
      let result: ProcessorResult<TOutput>;
      if (
        backendResult.code === ResponseCode.SUCCESS &&
        config.transformResponse &&
        backendResult.data
      ) {
        try {
          log.debug('Applying response transformation', { action });
          const transformedData = config.transformResponse(backendResult.data);
          result = {
            code: ResponseCode.SUCCESS,
            data: transformedData as TOutput,
          };
        } catch (error) {
          log.error('Response transformation failed', error, { action });
          result = { code: ResponseCode.INTERNAL_ERROR, data: undefined };
        }
      } else {
        // No transformation - cast backend data directly to TOutput
        result = backendResult as ProcessorResult<TOutput>;
      }

      // Step 6: Execute side effects if configured
      if (
        result.code === ResponseCode.SUCCESS &&
        config.executeSideEffect &&
        result.data
      ) {
        try {
          log.debug('Executing side effects', { action });
          await config.executeSideEffect(result.data, fullData);
        } catch (error) {
          // Log but don't fail - side effects are non-critical
          log.error('Side effect execution failed', error, { action });
        }
      }

      // Log result
      const isSuccess = this.isSuccessCode(result.code);
      if (isSuccess) {
        log.info('Action execution succeeded', {
          action,
          duration_ms: duration,
          code: result.code,
        });
      } else {
        log.warn('Action execution failed', {
          action,
          duration_ms: duration,
          code: result.code,
        });
      }

      // Update toast based on result
      if (shouldShowToast && currentToastId && toastMethods) {
        if (isSuccess) {
          const successMessage =
            customToast?.success || toastConfig.success || 'Success!';
          toastMethods.update(currentToastId, {
            type: ToastType.SUCCESS,
            title: successMessage,
            duration: 3000,
          });
        } else {
          const errorMessage =
            customToast?.error ||
            toastConfig.error ||
            getResponseCodeMessage(result.code);
          const resultMessage = getResponseCodeMessage(result.code);
          toastMethods.update(currentToastId, {
            type: ToastType.ERROR,
            title: errorMessage,
            message: resultMessage !== errorMessage ? resultMessage : undefined,
            duration: 5000,
          });
        }
      }

      // Step 5: Call result callback
      this.handleResult(result, options?.onResult);

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;

      log.error('Action execution error', error, {
        action,
        duration_ms: duration,
      });

      const result: ProcessorResult<TOutput> = {
        code: ResponseCode.INTERNAL_ERROR,
        data: undefined,
      };

      // Update toast on error
      if (shouldShowToast && currentToastId && toastMethods) {
        const errorMessage =
          customToast?.error ||
          toastConfig.error ||
          getResponseCodeMessage(result.code);
        toastMethods.update(currentToastId, {
          type: ToastType.ERROR,
          title: errorMessage,
          duration: 5000,
        });
      }

      // Call result callback
      this.handleResult(result, options?.onResult);

      return result;
    } finally {
      endRequest(requestId);
    }
  }

  /**
   * Convert AsyncPollResponse to ProcessorResult.
   * Network layer is separated from business layer which is nested under data field.
   */
  private convertPollResponseToResult<TOutput>(
    pollResponse: AsyncPollResponse<TOutput>
  ): ProcessorResult<TOutput> {
    // Handle FAILED status
    if (pollResponse.status === AsyncRequestStatus.FAILED) {
      return {
        code: ResponseCode.OPERATION_FAILED,
        data: undefined,
      };
    }

    // Handle COMPLETED with nested business response
    if (
      pollResponse.status === AsyncRequestStatus.COMPLETED &&
      pollResponse.data
    ) {
      const businessResponse = pollResponse.data;

      // Validate BusinessResponse structure
      if (
        businessResponse &&
        typeof businessResponse === 'object' &&
        'code' in businessResponse
      ) {
        // Map backend code to ResponseCode enum, preserving the actual code
        const rawCode = businessResponse.code as string;
        const code = Object.values(ResponseCode).includes(
          rawCode as ResponseCode
        )
          ? (rawCode as ResponseCode)
          : ResponseCode.OPERATION_FAILED;

        return {
          code,
          data: businessResponse.data as TOutput,
        };
      }

      // Fallback for unexpected structure
      logger.warn('Unexpected data structure in poll response', {
        data: businessResponse,
      });

      return {
        code: ResponseCode.INTERNAL_ERROR,
        data: undefined,
      };
    }

    // No data or incomplete status
    return {
      code: ResponseCode.INTERNAL_ERROR,
      data: undefined,
    };
  }

  /**
   * Map async status to progress percentage
   */
  private getProgressFromStatus(status: AsyncRequestStatus): number {
    switch (status) {
      case AsyncRequestStatus.SUBMITTED:
        return 10;
      case AsyncRequestStatus.IN_PROGRESS:
        return 50;
      case AsyncRequestStatus.COMPLETED:
        return 100;
      case AsyncRequestStatus.FAILED:
        return 100;
      default:
        return 0;
    }
  }

  /**
   * Check if a response code represents success.
   */
  private isSuccessCode(code: ResponseCode): boolean {
    return code === ResponseCode.SUCCESS;
  }

  /**
   * Handle processor result by calling the result callback.
   */
  private handleResult<TOutput>(
    result: ProcessorResult<TOutput>,
    callback?: ResultCallback<TOutput>
  ): void {
    if (!callback) {
      return;
    }

    const message = getResponseCodeMessage(result.code);
    callback(result.code, message, result.data);
  }
}

/**
 * Get singleton instance of ActionOrchestrator (convenience export)
 */
export const actionOrchestrator = ActionOrchestrator.getInstance();
